import { useEffect, useState } from "react";
import { useRouterState } from "@tanstack/react-router";
import { Phone } from "lucide-react";

import { telHref } from "@/components/site/PhoneLink";
import { mainPhone } from "@/lib/site-core";

/**
 * Sticky bottom bar on phones: "Get a Quick Quote" and "Call".
 *
 * Baseline, GA4 1-5 Oct 2026: on desktop the header CTA stays in view
 * (sticky header), but on phones no quote or call action stays in view
 * once the hero scrolls away, and no tel: link is above the fold.
 *
 * Rules:
 * - Phones only (md:hidden). Desktop keeps the sticky header CTA.
 * - Not on the quote form, Acuity booking, thank-you, patient
 *   instruction or admin pages (HIDDEN_PATHS).
 * - Hidden until the OneTrust state is known, and while the banner or
 *   the preference center is open. A tap elsewhere on the page while the
 *   banner is open counts as consent (zaraz-consent-bridge.ts), so the bar
 *   must never be a tap target at that time.
 * - Hidden while a form or a field is in view, and while a text field has
 *   focus, so it never sits over a field
 *   or over the on-screen keyboard.
 * - Fixed position and a transform to show and hide: no layout shift.
 *   The spacer is rendered on the server, at the end of the page.
 * - The quote link is relative, so the site's link decorator adds the
 *   stored UTM and click ID parameters (utm-tracking.ts). The Zaraz
 *   "Click to Quick Quote Pg 1" trigger (a[href*="quick-quote"]) sends
 *   click_to_qq1. The call link is counted by the global tel: listener
 *   with location "bar".
 */
const HIDDEN_PATHS =
  /^\/(quick-quote|schedule|thanks|contact-us\/(confirmation|scheduling-confirmation)|admin|auth|test(\/|$)|daybefore_sms|dayof_sms|sms-day-before|denver-post-op|tc-mobilesms)/;

function showsQuoteBar(pathname: string): boolean {
  return !HIDDEN_PATHS.test(pathname);
}

const BANNER_IDS = ["onetrust-banner-sdk", "onetrust-pc-sdk"];
// Wait this long for OneTrust before the bar shows anyway (no banner for
// GPC visitors, blocked scripts, preview hosts without Zaraz).
const CONSENT_WAIT_MS = 4000;
const POLL_MS = 400;

function isShown(el: HTMLElement): boolean {
  const s = window.getComputedStyle(el);
  if (s.display === "none" || s.visibility === "hidden" || Number(s.opacity) === 0) return false;
  const r = el.getBoundingClientRect();
  return r.height > 0 && r.width > 0;
}

function consentUiOpen(): boolean {
  return BANNER_IDS.some((id) => {
    const el = document.getElementById(id);
    return el ? isShown(el) : false;
  });
}

function bannerAlreadyClosed(): boolean {
  return document.cookie.indexOf("OptanonAlertBoxClosed") !== -1;
}

function isTextField(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  if (el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) return true;
  if (el instanceof HTMLInputElement) {
    return ![
      "checkbox",
      "radio",
      "button",
      "submit",
      "reset",
      "range",
      "color",
      "file",
      "image",
      "hidden",
    ].includes(el.type);
  }
  return el.isContentEditable;
}

export function MobileQuoteBar() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const allowed = showsQuoteBar(pathname);
  const [consentReady, setConsentReady] = useState(false);
  const [consentOpen, setConsentOpen] = useState(false);
  const [typing, setTyping] = useState(false);

  useEffect(() => {
    if (!allowed) return;
    const started = Date.now();
    let seenBanner = false;
    const check = () => {
      const open = consentUiOpen();
      if (open) seenBanner = true;
      setConsentOpen(open);
      if (bannerAlreadyClosed() || seenBanner || Date.now() - started >= CONSENT_WAIT_MS) {
        setConsentReady(true);
      }
    };
    check();
    const id = window.setInterval(check, POLL_MS);
    return () => window.clearInterval(id);
  }, [allowed]);

  useEffect(() => {
    if (!allowed) return;
    const onIn = (e: FocusEvent) => {
      if (isTextField(e.target)) setTyping(true);
    };
    const onOut = () => {
      // Focus can move straight to the next field; check after it lands.
      window.setTimeout(() => setTyping(isTextField(document.activeElement)), 50);
    };
    document.addEventListener("focusin", onIn);
    document.addEventListener("focusout", onOut);
    return () => {
      document.removeEventListener("focusin", onIn);
      document.removeEventListener("focusout", onOut);
    };
  }, [allowed]);

  // Hide while any form, or any field outside a form, is in view. Focus alone
  // is not enough: on /contact-us/ the bar covered #zip and #loc-search with
  // no field focused (verifier, 6 Oct 2026). New forms (client navigation,
  // late rendering) are picked up by the mutation observer.
  const [formInView, setFormInView] = useState(false);
  useEffect(() => {
    if (!allowed || typeof IntersectionObserver === "undefined") return;
    const inView = new Set<Element>();
    const watched = new WeakSet<Element>();
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) inView.add(e.target);
        else inView.delete(e.target);
      }
      setFormInView(inView.size > 0);
    });
    const scan = () => {
      const targets = document.querySelectorAll(
        'form, input:not([type="hidden"]), select, textarea',
      );
      targets.forEach((el) => {
        // Fields inside a form are covered by the form.
        if (el.tagName !== "FORM" && el.closest("form")) return;
        if (el.closest("[data-quote-bar]") || watched.has(el)) return;
        watched.add(el);
        io.observe(el);
      });
      // Drop elements that left the page.
      inView.forEach((el) => {
        if (!el.isConnected) inView.delete(el);
      });
      setFormInView(inView.size > 0);
    };
    scan();
    let queued = false;
    const mo = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        scan();
      });
    });
    mo.observe(document.body, { childList: true, subtree: true });
    return () => {
      mo.disconnect();
      io.disconnect();
    };
  }, [allowed, pathname]);

  if (!allowed) return null;
  const visible = consentReady && !consentOpen && !typing && !formInView;

  return (
    <>
      {/* Keeps the end of the footer clear of the bar. Server rendered, at
          the end of the document, so it moves nothing above it. */}
      <div
        aria-hidden="true"
        className="h-[calc(4.25rem+env(safe-area-inset-bottom,0px))] bg-muted md:hidden"
      />
      <div
        data-quote-bar=""
        data-cta-location="sticky_bar"
        data-visible={visible ? "1" : "0"}
        aria-hidden={visible ? undefined : true}
        inert={visible ? undefined : true}
        className={`fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 px-3 pt-2 pb-[calc(0.5rem+env(safe-area-inset-bottom,0px))] shadow-[0_-4px_12px_rgba(0,0,0,0.08)] backdrop-blur transition-transform duration-200 ease-out md:hidden ${
          visible ? "translate-y-0" : "translate-y-full"
        }`}
      >
        <div className="mx-auto flex max-w-xl gap-2">
          <a
            href="/quick-quote-part-1/"
            data-cta-location="bar"
            className="flex min-h-12 flex-1 items-center justify-center bg-primary px-4 text-center text-sm font-bold tracking-wider text-primary-foreground transition hover:bg-primary/90"
          >
            Get a Quick Quote
          </a>
          {/* CallRail may swap the number in this href before hydration (see
              PhoneLink.tsx). The label names no digits, so it cannot disagree
              with the number the link dials. */}
          <a
            href={telHref(mainPhone)}
            data-phone-location="bar"
            aria-label="Call RESTORE Hair"
            suppressHydrationWarning
            className="flex min-h-12 items-center justify-center gap-2 border-2 border-foreground bg-background px-5 text-sm font-bold tracking-wider text-foreground transition hover:bg-muted"
          >
            <Phone aria-hidden="true" className="h-4 w-4" />
            Call
          </a>
        </div>
      </div>
    </>
  );
}
